import { useEffect, useRef, useState } from "react";
import { Camera, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Avatar, CoverPhoto } from "@/components/Avatar";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_BYTES = 8 * 1024 * 1024;

const PhotoPicker = ({ label, hint, field, value, name, round, onSaved, testid }) => {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [pct, setPct] = useState(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Picture must be 8MB or smaller");
      return;
    }
    setPct(0);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", "");
    try {
      const { data } = await api.post("/images", form, {
        onUploadProgress: (evt) => setPct(evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0),
      });
      await onSaved(field, data.id);
      toast.success(`${label} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPct(null);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  return (
    <div className="border border-border bg-white p-4" data-testid={`${testid}-card`}>
      <div className="overline mb-1 text-slate-500">{label}</div>
      <p className="mb-3 text-xs text-slate-500">{hint}</p>
      {round ? (
        <Avatar imageId={value} name={name} size="xl" testid={`${testid}-preview`} />
      ) : (
        <CoverPhoto imageId={value} name={name} testid={`${testid}-preview`} className="h-28" />
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" data-testid={`${testid}-choose`} onClick={() => fileRef.current?.click()} disabled={pct !== null}>
          Choose a picture
        </Button>
        <Button size="sm" variant="outline" data-testid={`${testid}-camera`} onClick={() => camRef.current?.click()} disabled={pct !== null}>
          <Camera size={14} className="mr-1" /> Take a photo
        </Button>
        {value && (
          <Button size="sm" variant="ghost" data-testid={`${testid}-remove`} onClick={() => onSaved(field, "none")}>
            <Trash size={14} />
          </Button>
        )}
        {pct !== null && <span className="text-xs text-slate-500">Uploading {pct}%</span>}
      </div>
      <input ref={fileRef} data-testid={`${testid}-input`} type="file" accept="image/*" onChange={upload} className="hidden" />
      <input ref={camRef} data-testid={`${testid}-camera-input`} type="file" accept="image/*" capture="user" onChange={upload} className="hidden" />
    </div>
  );
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", company: "", phone: "", city: "", country: "", bio: "" });
  const [busy, setBusy] = useState(false);
  const isBusiness = ["vendor", "admin", "buyer"].includes(user?.role);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", company: user.company || "", phone: user.phone || "",
        city: user.city || "", country: user.country || "", bio: user.bio || "",
      });
    }
  }, [user]);

  const savePhoto = async (field, imageId) => {
    await api.patch("/me", { [field]: imageId });
    await refresh();
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.country) delete payload.country;
      await api.patch("/me", payload);
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <div className="relative mb-16">
        <CoverPhoto imageId={user?.cover_id} name={user?.name} testid="profile-cover" />
        <div className="absolute -bottom-12 left-4 flex items-end gap-3 sm:left-8">
          <Avatar imageId={user?.avatar_id} name={user?.name} size="lg" testid="profile-avatar" className="border-4 border-white" />
          <div className="pb-2">
            <div className="text-sm font-semibold text-[#0F172A]" data-testid="profile-name">{user?.name}</div>
            <div className="overline text-[0.6rem] text-slate-500">{user?.role}{user?.company ? ` · ${user.company}` : ""}</div>
          </div>
        </div>
      </div>

      <PageHeader
        overline="Account"
        title="My profile"
        description="Your picture and cover show up wherever you appear — the directory, the live feed, messages, applications and ratings."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <PhotoPicker
          testid="avatar-picker" label="Profile picture" round
          hint="A clear photo of you. Initials are shown until you add one."
          field="avatar_id" value={user?.avatar_id} name={user?.name} onSaved={savePhoto}
        />
        <PhotoPicker
          testid="cover-picker" label="Cover photo"
          hint="A wide shot of your workshop, yard or team."
          field="cover_id" value={user?.cover_id} name={user?.name} onSaved={savePhoto}
        />
        {isBusiness && (
          <>
            <PhotoPicker
              testid="logo-picker" label="Company logo" round
              hint="Used on your adverts, purchase orders and organisation card."
              field="logo_id" value={user?.logo_id} name={user?.company || user?.name} onSaved={savePhoto}
            />
            <PhotoPicker
              testid="company-cover-picker" label="Company cover"
              hint="The banner on your organisation card in the directory."
              field="company_cover_id" value={user?.company_cover_id} name={user?.company} onSaved={savePhoto}
            />
          </>
        )}
      </div>

      <div className="mt-4 border border-border bg-white p-4" data-testid="profile-details-card">
        <div className="overline mb-3 text-slate-500">Details</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="overline text-slate-500">Name</Label><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-none border-slate-300" /></div>
          <div><Label className="overline text-slate-500">Company</Label><Input data-testid="profile-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-none border-slate-300" /></div>
          <div><Label className="overline text-slate-500">Phone (with country code)</Label><Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 0100" className="mt-1.5 rounded-none border-slate-300" /></div>
          <div><Label className="overline text-slate-500">City</Label><Input data-testid="profile-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 rounded-none border-slate-300" /></div>
          <div>
            <Label className="overline text-slate-500">Country</Label>
            <Select value={form.country || "none"} onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="profile-country-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="none">Not set</SelectItem>
                {COUNTRIES.map(([code, label]) => <SelectItem key={code} value={code}>{code} — {label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="overline text-slate-500">About</Label><Textarea data-testid="profile-bio-input" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 rounded-none border-slate-300" /></div>
        </div>
        <Button data-testid="save-profile-details-button" disabled={busy} onClick={saveDetails} className="mt-4 rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
          Save profile
        </Button>
      </div>
    </div>
  );
}
